const cities = [
  { name: "Safety Harbor", zips: "34695" },
  { name: "Dunedin", zips: "34698" },
  { name: "Palm Harbor", zips: "34683" },
  { name: "Clearwater", zips: "33755" },
  { name: "Oldsmar", zips: "34677" },
  { name: "Tarpon Springs", zips: "34689" },
  { name: "Largo", zips: "33770" },
  { name: "St. Petersburg", zips: "33701" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home, the spa, or Philippe Park in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function SafetyHarborLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Safety Harbor & Pinellas County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Safety Harbor, Dunedin, Palm Harbor, or anywhere in northern Pinellas County, Fast Passport Center makes expedited passport processing simple — no drive to Tampa or Orlando, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Safety Harbor sits on the eastern shore of Old Tampa Bay like a secret that Tampa has not fully discovered. With a population of roughly 17,000, this small Pinellas County city punches far above its weight in character, history, and quality of life. Where Clearwater chases tourism and St. Petersburg chases culture, Safety Harbor has carved out something rarer: a genuinely peaceful waterfront community that feels removed from the bustle of the Tampa Bay metro area while sitting only 20 minutes from downtown Tampa and 25 minutes from Tampa International Airport.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city's identity is inseparable from its most famous landmark: the Safety Harbor Resort & Spa, a historic property built around natural mineral springs that indigenous peoples believed held healing properties. The resort anchors Safety Harbor's reputation as a wellness destination — visitors come for spa treatments, mineral baths, and waterfront yoga retreats. But for residents, Safety Harbor is simply home: a tree-lined downtown with locally owned restaurants, Philippe Park (the oldest park in Pinellas County, established in 1868), and miles of Tampa Bay shoreline that never feel crowded the way Clearwater Beach does.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Safety Harbor's demographics are distinct from its larger neighbors. The city attracts Tampa professionals who want a quieter commute home, retirees who value the waterfront lifestyle without the density of beach communities, and young families drawn to the excellent schools and small-town atmosphere. The median income is solidly middle-to-upper-middle class — not the extreme wealth of Belleair or the luxury towers of Clearwater Beach, but comfortable enough to support regular international travel. Many Safety Harbor residents work in Tampa's professional, medical, and financial corridors and travel internationally for business conferences, medical seminars, and client meetings.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city's proximity to Tampa International Airport makes it one of the most airport-accessible communities in the entire Tampa Bay region. TPA is consistently ranked among the best airports in the United States — compact, efficient, and offering direct international service to London, Frankfurt, Zurich, and seasonal Caribbean destinations. For Safety Harbor residents, this means international travel is not a major logistical undertaking. A family can leave their Philippe Park neighborhood, drive 25 minutes to TPA, and board a direct flight to Europe. That convenience makes passport issues all the more jarring when they appear: you are so close to the airport that discovering an expired passport feels like a cruel joke.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Safety Harbor also sits within easy driving distance of Port Tampa Bay, which handles a growing volume of Caribbean and Mexico cruise itineraries. Carnival, Royal Caribbean, and Norwegian all operate from Tampa, and Safety Harbor families regularly book spring break and summer cruises to the Bahamas, Cozumel, and the Western Caribbean. Cruise passengers face a particular passport trap: they often do not verify passport validity until receiving boarding documents, sometimes just days before sailing — far too late for standard processing.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Safety Harbor residents across every demographic — Tampa professionals with last-minute conference travel, retirees booking European river cruises, families with spring break Caribbean plans, and spa resort staff who need work authorization for international training programs. Our courier-based service means you start online from your Safety Harbor kitchen table or home office, ship overnight from any UPS or FedEx location in Dunedin, Palm Harbor, or Clearwater, and your passport arrives within days.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Safety Harbor — approximately 220 miles southeast via I-4 and I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Pinellas County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location in Safety Harbor, Dunedin, Palm Harbor, or Clearwater, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities & Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in northern Pinellas County and the greater Tampa Bay area — start online, ship overnight, and we handle the rest. No need to drive to Tampa or Orlando.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Tampa", href: "/tampa" },
                  { name: "St. Petersburg", href: "/st-petersburg" },
                  { name: "Clearwater", href: "/clearwater" },
                  { name: "Dunedin", href: "/dunedin" },
                  { name: "Palm Harbor", href: "/palm-harbor" },
                  { name: "Sarasota", href: "/sarasota" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}